import { useRef, useState } from "react";
import { normalizeShortcut } from "@/lib/cannedReplyUtils";
import ComposerLinkControl from "@/components/chat/ComposerLinkControl";
import { CannedResponseBodyPreview } from "@/lib/CannedResponseBodyPreview";
import { Banner, SectionHeading } from "../settings/settingsUi";
import { useMyCannedResponses } from "./hooks/useMyCannedResponses";

function emptyForm() {
  return { shortcut: "", title: "", body: "", visibility: "public" };
}

function formFromRow(row) {
  return {
    shortcut: row.shortcut || "",
    title: row.title || "",
    body: row.body || "",
    visibility: ["public", "pvt", "workspace_wide"].includes(row.visibility)
      ? row.visibility
      : "public",
  };
}

export default function CannedResponsesSettingsSection() {
  const {
    items,
    loading,
    error,
    refresh,
    updateCannedResponse,
    deleteCannedResponse,
    workspaceId,
  } = useMyCannedResponses();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const bodyTextareaRef = useRef(null);
  const previewRef = useRef(null);
  if (!previewRef.current) {
    previewRef.current = new CannedResponseBodyPreview();
  }
  const bodyPreview = previewRef.current;

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm(formFromRow(row));
    setFormError("");
    setBanner(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const shortcut = normalizeShortcut(form.shortcut);
    if (!shortcut) {
      setFormError("Add a shortcut (letters, numbers, hyphen only).");
      return;
    }
    if (!form.title.trim()) {
      setFormError("Add a title for this saved reply.");
      return;
    }
    if (!form.body.trim()) {
      setFormError("Add the message body.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      await updateCannedResponse({
        id: editingId,
        shortcut,
        title: form.title.trim(),
        body: form.body.trim(),
        visibility: form.visibility,
      });
      setBanner({ type: "ok", text: "Saved reply updated." });
      cancelEdit();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not update saved reply.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const label = row.title || `/${row.shortcut}`;
    if (!window.confirm(`Delete saved reply “${label}”? This cannot be undone.`)) return;
    try {
      await deleteCannedResponse(row.id);
      if (editingId === row.id) cancelEdit();
      setBanner({ type: "ok", text: "Saved reply deleted." });
    } catch (err) {
      setBanner({
        type: "err",
        text: err instanceof Error ? err.message : "Could not delete saved reply.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Saved replies</h3>
        <p className="mt-1 text-sm text-slate-500">
          Replies you created in this workspace. Edit or delete them here; create new ones from the inbox composer.
        </p>
      </div>

      <div className="ui-card-static !p-6 sm:!p-8">
        <SectionHeading
          label="Your canned responses"
          desc="Public replies are visible in this workspace. Private replies are only visible to you. Workspace-wide replies follow you across workspaces where you are enrolled."
        />

        {banner && (
          <div className="mb-4">
            <Banner type={banner.type} text={banner.text} onDismiss={() => setBanner(null)} />
          </div>
        )}

        {!workspaceId && (
          <p className="text-sm text-amber-800 dark:text-amber-300">Select a workspace to manage saved replies.</p>
        )}

        {workspaceId && loading && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading saved replies…</p>
        )}

        {workspaceId && error && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/80 px-3 py-2 text-sm text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">
            <span>{error.message || "Could not load saved replies."}</span>
            <button type="button" onClick={() => void refresh()} className="font-semibold underline">
              Retry
            </button>
          </div>
        )}

        {workspaceId && !loading && !error && items.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            You have not created any saved replies in this workspace yet. Type{" "}
            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[11px] dark:border-slate-600 dark:bg-slate-800">
              /
            </kbd>{" "}
            in the inbox composer or use <strong>Add saved reply</strong> there.
          </p>
        )}

        {workspaceId && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((row) => {
              const isEditing = editingId === row.id;
              return (
                <li
                  key={row.id}
                  className="rounded-xl border border-slate-200/80 bg-white p-3 dark:border-slate-700/60 dark:bg-slate-900/50"
                >
                  {!isEditing ? (
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 rounded-md bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white dark:bg-slate-700">
                        /{row.shortcut}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{row.title}</p>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              row.visibility === "pvt"
                                ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200/80 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800/60"
                                : row.visibility === "workspace_wide"
                                  ? "bg-violet-50 text-violet-800 ring-1 ring-violet-200/80 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800/60"
                                  : "bg-teal-50 text-teal-800 ring-1 ring-teal-200/80 dark:bg-teal-950/40 dark:text-teal-200 dark:ring-teal-800/60"
                            }`}
                          >
                            {row.visibility === "pvt"
                              ? "Private"
                              : row.visibility === "workspace_wide"
                                ? "Workspace-wide"
                                : "Public"}
                          </span>
                        </div>
                        {bodyPreview.hasContent(row.body) ? (
                          <div
                            className="mt-1 line-clamp-3 text-[13px] text-slate-500 dark:text-slate-400 [&_a]:text-teal-700 [&_a]:underline"
                            dangerouslySetInnerHTML={{ __html: bodyPreview.toHtml(row.body) }}
                          />
                        ) : null}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => startEdit(row)}
                          className="ui-btn-icon !h-8 !w-8 text-slate-400 hover:text-teal-700"
                        >
                          <i className="ri-pencil-line text-base" aria-hidden />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => void handleDelete(row)}
                          className="ui-btn-icon !h-8 !w-8 text-slate-400 hover:text-rose-600"
                        >
                          <i className="ri-delete-bin-line text-base" aria-hidden />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSave} className="space-y-3">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Shortcut
                          </span>
                          <div className="flex items-center rounded-lg border border-slate-200 bg-white px-2 ring-1 ring-transparent focus-within:border-teal-300 focus-within:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800">
                            <span className="text-sm text-slate-400 dark:text-slate-500">/</span>
                            <input
                              value={form.shortcut}
                              onChange={(e) => setForm((f) => ({ ...f, shortcut: e.target.value }))}
                              className="w-full border-0 bg-transparent py-1.5 pl-0.5 text-sm outline-none dark:text-slate-200"
                              placeholder="e.g. refund"
                              autoComplete="off"
                            />
                          </div>
                        </label>
                        <label className="block">
                          <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Title
                          </span>
                          <input
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none ring-1 ring-transparent focus:border-teal-300 focus:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                            placeholder="Shown in picker"
                          />
                        </label>
                      </div>
                      <label className="block">
                        <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Message
                        </span>
                        <textarea
                          ref={bodyTextareaRef}
                          value={form.body}
                          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                          rows={4}
                          className="w-full resize-none rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none ring-1 ring-transparent focus:border-teal-300 focus:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                          placeholder={"Hi {{first_name}},\n\nInclude links as [label](https://example.com)"}
                        />
                        <div className="mt-1.5 flex items-center gap-2">
                          <ComposerLinkControl
                            disabled={saving}
                            textareaRef={bodyTextareaRef}
                            value={form.body}
                            onChange={(next) => setForm((f) => ({ ...f, body: next }))}
                          />
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">
                            Add a hyperlink to the saved reply
                          </span>
                        </div>
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Visibility
                        </span>
                        <label className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name={`visibility-${row.id}`}
                            value="public"
                            checked={form.visibility === "public"}
                            onChange={() => setForm((f) => ({ ...f, visibility: "public" }))}
                            className="accent-teal-600"
                          />
                          <span className="text-xs text-slate-700 dark:text-slate-300">Public — all agents</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name={`visibility-${row.id}`}
                            value="pvt"
                            checked={form.visibility === "pvt"}
                            onChange={() => setForm((f) => ({ ...f, visibility: "pvt" }))}
                            className="accent-teal-600"
                          />
                          <span className="text-xs text-slate-700 dark:text-slate-300">Private — only me</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name={`visibility-${row.id}`}
                            value="workspace_wide"
                            checked={form.visibility === "workspace_wide"}
                            onChange={() => setForm((f) => ({ ...f, visibility: "workspace_wide" }))}
                            className="accent-teal-600"
                          />
                          <span className="text-xs text-slate-700 dark:text-slate-300">
                            Workspace-wide — all my workspaces
                          </span>
                        </label>
                      </div>
                      {form.visibility === "workspace_wide" && (
                        <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                          Available to agents in every workspace where you are enrolled. Access is removed from a workspace if you leave it.
                        </p>
                      )}
                      {formError && <p className="text-xs font-medium text-rose-600 dark:text-rose-400">{formError}</p>}
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
                        >
                          Cancel
                        </button>
                        <button type="submit" disabled={saving} className="ui-btn-primary !py-1.5 !text-xs disabled:opacity-50">
                          {saving ? "Saving…" : "Save changes"}
                        </button>
                      </div>
                    </form>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
